/**
 * Очередь ЮСКАРа: видео, присланные игроками, которые DC должен посмотреть и
 * либо взять в номинацию, либо оставить.
 *
 * Разобранные не удаляются, а уезжают вниз и гаснут: очередь читается сверху,
 * но остаётся видно, кто что прислал и куда это ушло.
 * Вынесено из DcYuskarBlock — тот упирался в лимит длины файла.
 */
import type { ReactElement } from "react";
import { NUM, VideoThumb, capTitle, fmt } from "./dcVideoStatsParts";
import type { YuskarPreview } from "./DcYuskarPreview";

export interface YuskarSubmission {
  materialId: number; bookingId: number; label: string; duration: string; game: string;
  tg: number; name: string; username: string; avatarUrl: string; identified: boolean;
  streamUrl: string; posterUrl: string; sentAt: string; changedAt: string; replaced: boolean;
}

interface Nomination { id: string; title: string; candidates: { id: string }[] }

const when = (iso: string): string => iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${iso.slice(11, 16)}` : "—";

const sender = (s: YuskarSubmission): string =>
  s.identified ? (s.name || (s.username ? "@" + s.username : "id " + s.tg)) : "неопознанный отправитель";

export function DcYuskarQueue({ submissions, nominations, onOpen }: {
  submissions: YuskarSubmission[];
  nominations: Nomination[];
  onOpen: (p: YuskarPreview) => void;
}): ReactElement | null {
  if (submissions.length === 0) return null;

  const takenBy = (sub: YuskarSubmission): Nomination[] =>
    nominations.filter((n) => n.candidates.some((cd) => cd.id === `mat-${sub.materialId}`));

  const preview = (sub: YuskarSubmission): YuskarPreview => ({
    id: `mat-${sub.materialId}`,
    label: sub.label || `Видео #${sub.materialId}`,
    src: sub.streamUrl, poster: sub.posterUrl,
    game: sub.game, bookingId: sub.bookingId,
    materialId: sub.materialId,
    sender: sub.identified ? sender(sub) : "",
    duration: sub.duration,
    takenIn: takenBy(sub).map((n) => n.id),
  });

  const ordered = [...submissions]
    .sort((a, b) => Number(takenBy(a).length > 0) - Number(takenBy(b).length > 0));
  const pending = submissions.length - submissions.filter((s) => takenBy(s).length > 0).length;

  return (
    <div style={{ display: "grid", gap: 3, paddingTop: 4, borderTop: "1px solid var(--surface-3)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <span style={{ ...capTitle, fontSize: 9.5, color: "var(--role)" }}>Своё видео в ЮСКАР · от игроков</span>
        <span style={{ ...NUM, fontSize: 10, color: "var(--text-3)" }}>
          {pending ? `${fmt(pending)} не разобрано из ${fmt(submissions.length)}` : `${fmt(submissions.length)} · все разобраны`}
        </span>
      </div>

      {ordered.map((sub, i) => {
        const taken = takenBy(sub);
        return (
          <button key={sub.materialId} type="button" onClick={() => onOpen(preview(sub))}
            title="Посмотреть ролик и взять в номинацию"
            style={{ display: "grid", gridTemplateColumns: "44px 1fr auto", gap: 8, alignItems: "center", width: "100%", minHeight: 34, padding: "3px 2px", border: "none", borderTop: i ? "1px solid var(--surface-3)" : "none", background: "transparent", color: "var(--text-1)", cursor: "pointer", textAlign: "left", opacity: taken.length ? 0.55 : 1 }}>
            <VideoThumb url={sub.posterUrl} w={44} h={28} />
            <span style={{ minWidth: 0 }}>
              <span style={{ fontSize: 11.5, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                ▶ {sender(sub)}
                {sub.replaced ? <span style={{ color: "var(--text-3)" }}> · заменил</span> : null}
              </span>
              <span style={{ ...NUM, fontSize: 9, color: "var(--text-3)" }}>
                {sub.game || "игра неизвестна"}{sub.duration ? ` · ${sub.duration}` : ""}
              </span>
            </span>
            <span style={{ display: "grid", gap: 3, justifyItems: "end" }}>
              <span style={{ ...NUM, fontSize: 9.5, color: "var(--text-3)" }}>{when(sub.changedAt)}</span>
              <span style={{ ...NUM, fontSize: 9.5, fontWeight: 700, color: taken.length ? "var(--role)" : "var(--text-3)" }}>
                {taken.length ? `✓ ${taken.map((n) => n.title).join(", ")}` : "не в номинации"}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
